import React from 'react';
import { Tr, Td, Text, HStack, Icon, Skeleton } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

import type {
  AvailableCarrier,
  CarrierQuote,
  CarriersLocation,
  CarriersInsuranceHistory,
} from 'api/quote-marketplace';
import type { QuoteMarketplaceTheme } from '../../hooks';
import { useAnswerCodes } from '../../hooks/useAnswerCodes';
import {
  CarrierCell,
  STICKY_LABEL_WIDTH,
  STICKY_REQUESTED_WIDTH,
} from './CarrierCell';
import { CoverageMatchIndicator } from './CoverageMatchIndicator';
import {
  CORE_COVERAGE_ROWS,
  INDICATOR_ROWS,
  COVERAGE_SECTIONS,
  DISCOUNTS_SECTION,
  type CoverageRowConfig,
  type CoverageSectionConfig,
  type DiscountRowConfig,
} from '../../constants/habitationalCarrierCoverages';
import { getMatchStatus } from '../../utils/coverageMatchStatus';

interface LocationCoveragesSectionProps {
  location: CarriersLocation;
  locationIndex: number;
  availableCarriers: AvailableCarrier[];
  carrierQuotes: CarrierQuote[];
  selectedCarrierQuoteId: number | null;
  expandedSections: string[];
  isLoading: boolean;
  theme: QuoteMarketplaceTheme;
  onToggleSection: (section: string) => void;
  insuranceHistory: CarriersInsuranceHistory;
}

export const LocationCoveragesSection = ({
  location,
  locationIndex,
  availableCarriers,
  carrierQuotes,
  selectedCarrierQuoteId,
  expandedSections,
  isLoading,
  theme,
  onToggleSection,
  insuranceHistory,
}: LocationCoveragesSectionProps) => {
  const { getLabel } = useAnswerCodes();
  const coverages = location.coverages;
  const coverageFormLabel =
    getLabel(coverages.coverage_form_type, 'location_form_type') || '-';

  const getQuoteForCarrier = (
    carrier: AvailableCarrier,
  ): CarrierQuote | undefined => {
    return carrierQuotes.find((q) => q.acturis_product === carrier.product);
  };

  const isCarrierSelected = (carrier: AvailableCarrier): boolean => {
    const quote = getQuoteForCarrier(carrier);
    return quote?.id === selectedCarrierQuoteId;
  };

  const getRatedCoverageForLocation = (
    carrier: AvailableCarrier,
  ): Record<string, unknown> | null => {
    const quote = getQuoteForCarrier(carrier);
    if (!quote?.rated_coverages) return null;

    const locations = quote.rated_coverages.locations as
      | Record<string, unknown>[]
      | undefined;
    const locationCoverages = locations?.[locationIndex] || {};

    const liability = quote.rated_coverages.liability as
      | Record<string, unknown>
      | undefined;

    return {
      ...locationCoverages,
      ...(liability || {}),
    };
  };

  const renderRatedValueWithIndicator = (
    carrier: AvailableCarrier,
    row: CoverageRowConfig,
  ) => {
    const quote = getQuoteForCarrier(carrier);

    if ((!quote || quote.status === 'pending') && isLoading) {
      return <Skeleton height="16px" width="50px" mx="auto" />;
    }

    if (!quote || quote.status === 'pending') {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out'
    ) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    const ratedCoverages = getRatedCoverageForLocation(carrier);
    const isRequested = row.isRequested
      ? row.isRequested(coverages, location)
      : true;

    const rawRequested = row.getRawRequestedValue
      ? row.getRawRequestedValue(coverages, location)
      : null;
    const rawRated =
      row.getRawRatedValue && ratedCoverages
        ? row.getRawRatedValue(ratedCoverages)
        : null;

    const status = getMatchStatus(rawRequested, rawRated, isRequested);

    const ratedValue =
      row.getRatedValue && ratedCoverages
        ? row.getRatedValue(ratedCoverages)
        : undefined;

    return (
      <CoverageMatchIndicator status={status} value={ratedValue || null} />
    );
  };

  const renderRatedValue = (
    carrier: AvailableCarrier,
    getValue:
      | ((ratedCoverages: Record<string, unknown>) => string | undefined)
      | undefined,
  ) => {
    const quote = getQuoteForCarrier(carrier);

    if ((!quote || quote.status === 'pending') && isLoading) {
      return <Skeleton height="16px" width="50px" mx="auto" />;
    }

    if (!quote || quote.status === 'pending') {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out'
    ) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (!getValue) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    const ratedCoverages = getRatedCoverageForLocation(carrier);
    const ratedValue = ratedCoverages ? getValue(ratedCoverages) : undefined;

    if (!ratedValue) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    return (
      <Text fontSize="sm" fontWeight="500">
        {ratedValue}
      </Text>
    );
  };

  const renderCoverageRow = (
    row: CoverageRowConfig,
    indentLevel: number = 1,
  ) => {
    const requestedValue = row.getRequestedValue(coverages, location);
    const paddingLeft = indentLevel === 1 ? 8 : 12;
    const hasIndicatorSupport =
      row.getRawRequestedValue && row.getRawRatedValue && row.isRequested;

    return (
      <Tr key={row.label} _hover={{ bg: theme.hoverBg }}>
        <Td
          position="sticky"
          left={0}
          bg={theme.bgColor}
          borderColor={theme.borderColor}
          py={3}
          pl={paddingLeft}
          zIndex={2}
        >
          <Text fontSize="sm">{row.label}</Text>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          textAlign="center"
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        >
          <Text fontSize="sm" fontWeight="500">
            {requestedValue}
          </Text>
        </Td>
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            >
              {hasIndicatorSupport
                ? renderRatedValueWithIndicator(carrier, row)
                : renderRatedValue(carrier, row.getRatedValue)}
            </CarrierCell>
          );
        })}
      </Tr>
    );
  };

  const renderSectionHeader = (
    section: CoverageSectionConfig | { key: string; label: string },
  ) => {
    const isExpanded = expandedSections.includes(section.key);
    return (
      <Tr
        onClick={() => onToggleSection(section.key)}
        cursor="pointer"
        _hover={{ bg: theme.hoverBg }}
        bg={theme.sectionHeaderBg}
      >
        <Td
          position="sticky"
          left={0}
          bg={theme.sectionHeaderBg}
          borderColor={theme.borderColor}
          py={3}
          zIndex={2}
        >
          <HStack spacing={2}>
            <Icon
              as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
              boxSize={4}
              color={theme.sectionHeaderColor}
            />
            <Text
              fontSize="sm"
              fontWeight="500"
              color={theme.sectionHeaderColor}
            >
              {section.label}
            </Text>
          </HStack>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        />
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            />
          );
        })}
      </Tr>
    );
  };

  const renderDiscountRatedValueWithIndicator = (
    carrier: AvailableCarrier,
    row: DiscountRowConfig,
  ) => {
    const quote = getQuoteForCarrier(carrier);

    if ((!quote || quote.status === 'pending') && isLoading) {
      return <Skeleton height="16px" width="50px" mx="auto" />;
    }

    if (!quote || quote.status === 'pending') {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    if (
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out'
    ) {
      return (
        <Text color={theme.labelColor} fontSize="sm">
          -
        </Text>
      );
    }

    const ratedCoverages = getRatedCoverageForLocation(carrier);
    const isRequested = row.isRequested(location, insuranceHistory);

    const rawRequested = row.getRawRequestedValue(location, insuranceHistory);
    const rawRated = ratedCoverages
      ? row.getRawRatedValue(ratedCoverages)
      : null;

    const status = getMatchStatus(rawRequested, rawRated, isRequested);

    const ratedValue = ratedCoverages
      ? row.getRatedValue(ratedCoverages)
      : undefined;

    return (
      <CoverageMatchIndicator status={status} value={ratedValue || null} />
    );
  };

  const renderDiscountRow = (row: DiscountRowConfig) => {
    const requestedValue = row.getRequestedValue(location, insuranceHistory);

    return (
      <Tr key={row.label} _hover={{ bg: theme.hoverBg }}>
        <Td
          position="sticky"
          left={0}
          bg={theme.bgColor}
          borderColor={theme.borderColor}
          py={3}
          pl={12}
          zIndex={2}
        >
          <Text fontSize="sm">{row.label}</Text>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          textAlign="center"
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        >
          <Text fontSize="sm" fontWeight="500">
            {requestedValue}
          </Text>
        </Td>
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            >
              {renderDiscountRatedValueWithIndicator(carrier, row)}
            </CarrierCell>
          );
        })}
      </Tr>
    );
  };

  return (
    <>
      <Tr _hover={{ bg: theme.hoverBg }}>
        <Td
          position="sticky"
          left={0}
          bg={theme.bgColor}
          borderColor={theme.borderColor}
          py={3}
          pl={8}
          zIndex={2}
        >
          <Text fontSize="sm">Coverage Form</Text>
        </Td>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          textAlign="center"
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        >
          <Text fontSize="sm" fontWeight="500">
            {coverageFormLabel}
          </Text>
        </Td>
        {availableCarriers.map((carrier, index) => {
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              isSticky={index === 0 && isSelected}
            />
          );
        })}
      </Tr>
      {CORE_COVERAGE_ROWS.map((row) => renderCoverageRow(row))}
      {INDICATOR_ROWS.map((row) => renderCoverageRow(row))}
      {COVERAGE_SECTIONS.map((section) => (
        <React.Fragment key={section.key}>
          {renderSectionHeader(section)}
          {expandedSections.includes(section.key) &&
            section.rows.map((row) => renderCoverageRow(row, 2))}
        </React.Fragment>
      ))}
      {renderSectionHeader(DISCOUNTS_SECTION)}
      {expandedSections.includes(DISCOUNTS_SECTION.key) &&
        DISCOUNTS_SECTION.rows.map((row) => renderDiscountRow(row))}
    </>
  );
};

export type { LocationCoveragesSectionProps };
